/**
 * ReportingTab — Charts + event reason table + recent events table (area version).
 *
 * Reuses existing V1 chart components:
 *   - HourlyLineAndBarChart (alarm activity by hour)
 *   - LineTrendingChart (weekly alarm trend)
 *
 * Displays event reason breakdown and recent events in dp-table format.
 * Area version shows "Device" column instead of "Location" (since we're on the area).
 */

import { useMemo, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HourlyLineAndBarChart from '../../../../../components/charts/HourlyLineAndBarChart.jsx';
import LineTrendingChart from '../../../../../components/charts/LineTrendingChart.jsx';
import RunReportButton from '../../../../../components/v2/RunReportButton.jsx';
import NotesCell from '../../../../../components/v2/NotesCell.jsx';
import CallsCell from '../../../../../components/v2/CallsCell.jsx';
import { convertIsoToLocal, getDeviceProfileLink } from '../../../../../libs.jsx';

const HOURLY_TOOLTIP = [
  'This chart shows the average number of alerts per hour of the day (bars) and the average caregiver response time, in minutes, for those alerts (line) for this area over the last 28 days.',
  'The dashed horizontal lines mark the overall averages across all hours so you can quickly spot which times of day run higher or lower than normal.',
];

const WEEKLY_TOOLTIP = [
  'This chart shows the total number of alerts triggered in this area each week over the last 12 weeks, so you can spot trends in alert volume over time.',
];

const REASON_TOOLTIP = [
  'This table breaks down events triggered in this area over the last 28 days by reason code,',
  'showing the count, share of total volume, and average caregiver response time (in minutes) for each reason.',
  'The Total Events row at the top is the rolled-up total across all reasons.',
];

const RECENT_EVENTS_TOOLTIP = [
  'This table lists the 25 most recent events for this area, regardless of date range,',
  'so you can quickly review the latest activity and jump to any individual event for more detail.',
];

/* System-generated close reasons. -1 = autoclear timeout, -2 = auto finish timeout. */
const AUTO_CLOSE_REASON_MESSAGES = {
  '-1': 'This event was automatically closed by the system due to autoclear timeout.',
  '-2': 'This event was automatically closed by the system due to auto finish timeout.',
};

/* ── ReasonCell — renders the reason text. When the reason is an auto-close
   sentinel (-1 or -2) it gets a dotted underline and a hover popover that
   explains why the event was closed. ── */
const ReasonCell = ({ reasonId, description, edited }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const message = AUTO_CLOSE_REASON_MESSAGES[String(reasonId)];
  const text = description || '\u2014';

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  if (!message) {
    return (
      <span className="dp-cell-text">
        {text}
        {edited ? (
          <span title="This value has been edited" style={{ marginLeft: '4px', color: 'var(--color-warning, #d9a406)' }}>*</span>
        ) : null}
      </span>
    );
  }

  const handleEnter = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popoverW = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    let left = centerX - popoverW / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));
    setStyle({
      position: 'fixed',
      zIndex: 9999,
      left: `${left}px`,
      top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      width: `${popoverW}px`,
      whiteSpace: 'normal',
    });
    setOpen(true);
  };

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)} style={{ position: 'relative', display: 'inline-block' }}>
      <span className="dp-cell-text has-popover">{text}</span>
      {edited ? (
        <span title="This value has been edited" style={{ marginLeft: '4px', color: 'var(--color-warning, #d9a406)' }}>*</span>
      ) : null}
      {open ? (
        <span className="na-type-popover" style={style}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{message}</span>
        </span>
      ) : null}
    </span>
  );
};

/* ── HeaderInfoTip — generic ⓘ icon with hover popover ── */
const HeaderInfoTip = ({ title, body }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [style, setStyle] = useState({});

  const handleEnter = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const spaceRight = window.innerWidth - rect.left;
    const spaceBelow = window.innerHeight - rect.bottom;

    setStyle({
      position: 'fixed',
      left: spaceRight < 320 ? undefined : `${rect.left}px`,
      right: spaceRight < 320 ? '16px' : undefined,
      top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      maxWidth: '300px',
      whiteSpace: 'normal',
    });
    setOpen(true);
  };

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)} style={{ cursor: 'help', marginLeft: '6px', color: 'var(--color-text-muted)' }}>
      <i className="fe fe-info" style={{ fontSize: 'var(--font-size-sm)' }} />
      {open && (
        <span className="na-type-popover" style={style}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>{title}</span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{body}</span>
        </span>
      )}
    </span>
  );
};

const ReportingTab = ({
  hourData, weekData, reasonData, eventData,
  eventNotesActive, isReasonEditable, isGrandstream, tzOffset,
  loading, onAddNote, areaId, areaName,
}) => {
  // Reason breakdown rows
  const reasonRows = useMemo(() => {
    if (!reasonData?.reasons) return [];
    return reasonData.reasons;
  }, [reasonData]);

  // Weekly chart data (formatted for LineTrendingChart)
  const weekChartProps = useMemo(() => ({
    lineData: [weekData?.events?.length > 0 ? weekData.events : []],
    lineColors: ['#0081C7'],
    lineLabels: ['Total Alarms'],
    axisLabels: weekData?.labels?.length > 0 ? weekData.labels : [],
  }), [weekData]);

  return (
    <>
      {/* Report action + summary stats */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-success-text)', fontWeight: 'var(--font-weight-semibold)' }}>
          {!isNaN(hourData?.avgValue) ? `${hourData.avgValue} Alert/Week` : ''}
          {!isNaN(weekData?.avgValue) ? ` · ${weekData.avgValue} Avg Resp` : ''}
        </span>
        <RunReportButton filters={{ areas: [areaId] }} />
      </div>

      {/* Charts — separate cards in a responsive grid */}
      <div className="ip-chart-row" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Alarm Activity by Hour<HeaderInfoTip title="About This Chart" body={HOURLY_TOOLTIP.join(' ')} /></h3>
          </div>
          <div className="pp-section__body">
            <div className="ip-chart-section">
              <HourlyLineAndBarChart
                style={{ maxHeight: '350px' }}
                labels={hourData?.labels || ['0','1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','17','18','19','20','21','22','23']}
                x_axis={true}
                x_axis_title="Time of the Day"
                bar_axis={true}
                bar_axis_title="Total Alarms"
                bar_color="#0081C7"
                line_axis={true}
                line_axis_title="Average Response Time"
                line_color="#BE4D25"
                bar_data={hourData?.events || []}
                line_data={hourData?.responses || []}
                bar_annotation={true}
                line_annotation={true}
                forceAspect={true}
                bar_annotation_value={hourData?.avgValue || 0}
                line_annotation_value={hourData?.avgResp || 0}
              />
            </div>
          </div>
        </div>

        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Weekly Alarm Trend<HeaderInfoTip title="About This Chart" body={WEEKLY_TOOLTIP.join(' ')} /></h3>
          </div>
          <div className="pp-section__body">
            <div className="ip-chart-section">
              <LineTrendingChart
                style={{ maxHeight: '350px' }}
                x_axis={true}
                x_axis_title="Weeks"
                line_axis={true}
                line_axis_title="Total Alarms"
                lines={1}
                line_color={weekChartProps.lineColors}
                line_labels={weekChartProps.lineLabels}
                line_data={weekChartProps.lineData}
                forceAspect={true}
                labels={weekChartProps.axisLabels}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Event Reason Breakdown + Recent Events */}
      <div className="ip-report-grid">
        {/* Event Reason Breakdown */}
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Event Reason Breakdown<HeaderInfoTip title="About This Table" body={REASON_TOOLTIP.join(' ')} /></h3>
          </div>
          <div className="pp-section__body pp-section__body--flush">
            <div className="dp-table-wrap">
              <table className="dp-table">
                <thead className="dp-thead">
                  <tr>
                    <th className="dp-th">Reason</th>
                    <th className="dp-th" style={{ textAlign: 'center' }}>Total</th>
                    <th className="dp-th" style={{ textAlign: 'center' }}>% Total Volume</th>
                    <th className="dp-th" style={{ textAlign: 'center' }}>Avg Response (min)</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Summary row */}
                  <tr className="dp-tr ip-reason-total">
                    <td className="dp-td"><span className="dp-cell-text">Total Events</span></td>
                    <td className="dp-td" style={{ textAlign: 'center' }}>
                      <span className="dp-cell-text">{reasonData?.count || 0}</span>
                    </td>
                    <td className="dp-td" style={{ textAlign: 'center' }}></td>
                    <td className="dp-td" style={{ textAlign: 'center' }}>
                      <span className="dp-cell-text">{reasonData?.avg_response_time || '0.0'}</span>
                    </td>
                  </tr>
                  {/* Detail rows */}
                  {reasonRows.map((row, idx) => (
                    <tr key={idx} className="dp-tr">
                      <td className="dp-td"><span className="dp-cell-text">{row.reason_name || '—'}</span></td>
                      <td className="dp-td" style={{ textAlign: 'center' }}>
                        <span className="dp-cell-text">{row.count || 0}</span>
                      </td>
                      <td className="dp-td">
                        <div className="ip-percent-bar">
                          <div className="ip-percent-bar__track">
                            <div
                              className="ip-percent-bar__fill"
                              style={{ width: `${parseFloat(row.percent) || 0}%` }}
                            />
                          </div>
                          <span className="ip-percent-bar__label">{row.percent || '—'}</span>
                        </div>
                      </td>
                      <td className="dp-td" style={{ textAlign: 'center' }}>
                        <span className="dp-cell-text">{row.avg_response_time || '—'}</span>
                      </td>
                    </tr>
                  ))}
                  {reasonRows.length === 0 && (
                    <tr className="dp-tr">
                      <td className="dp-td" colSpan={4} style={{ textAlign: 'center' }}>
                        <span className="dp-cell-text dp-cell-text--muted">No event data for this period</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Recent Events */}
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Recent Events<HeaderInfoTip title="About This Table" body={RECENT_EVENTS_TOOLTIP.join(' ')} /></h3>
          </div>
          <div className="pp-section__body pp-section__body--flush" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <div className="dp-table-wrap">
              <table className="dp-table">
                <thead className="dp-thead">
                  <tr>
                    <th className="dp-th">Event Start</th>
                    <th className="dp-th" style={{ textAlign: 'center' }}>Resp (min)</th>
                    <th className="dp-th">Device</th>
                    <th className="dp-th">Resident</th>
                    <th className="dp-th" style={{ textAlign: 'center' }}>Reason</th>
                    {eventNotesActive && <th className="dp-th" style={{ textAlign: 'center' }}>Notes</th>}
                    {isGrandstream && <th className="dp-th" style={{ textAlign: 'center' }}>Calls</th>}
                  </tr>
                </thead>
                <tbody>
                  {eventData.map((evt, idx) => (
                    <tr key={evt.event_id || idx} className="dp-tr">
                      <td className="dp-td td-wrap">
                        <Link to={`/profiles/event/${evt.event_id}`} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>
                          {evt.event_datetime_local ? convertIsoToLocal(evt.event_datetime_local) : '—'}
                        </Link>
                      </td>
                      <td className="dp-td" style={{ textAlign: 'center' }}>
                        <span className="dp-cell-text">{evt.response_time ?? '—'}</span>
                      </td>
                      <td className="dp-td td-wrap">
                        {(() => {
                          const deviceLink = getDeviceProfileLink(evt);
                          const uuidSub = evt.manufacture_uuid ? (
                            <span className="dp-cell-text dp-cell-text--muted" style={{ display: 'block', fontSize: 'var(--font-size-xs)' }}>
                              {evt.manufacture_uuid}
                            </span>
                          ) : null;
                          // Link only for devices with a profile page (mirrors DevicesPage);
                          // otherwise show the device name (or — when null) as plain text.
                          return deviceLink ? (
                            <Link to={deviceLink} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>
                              <span>{evt.device_name || '—'}</span>
                              {uuidSub}
                            </Link>
                          ) : (
                            <div>
                              <span className="dp-cell-text">{evt.device_name || '—'}</span>
                              {uuidSub}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="dp-td td-wrap">
                        {evt.resident_id ? (
                          <Link to={`/community/residents/${evt.resident_id}`} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>
                            {evt.resident_name || '—'}
                          </Link>
                        ) : (
                          <span className="dp-cell-text dp-cell-text--muted">—</span>
                        )}
                      </td>
                      <td className="dp-td td-wrap" style={{ textAlign: 'center' }}>
                        <ReasonCell
                          reasonId={evt.reason_id}
                          description={evt.reason_code_description}
                          edited={isReasonEditable && evt.reason_edits?.length > 0}
                        />
                      </td>
                      {eventNotesActive && (
                        <td className="dp-td" style={{ textAlign: 'center' }}>
                          <NotesCell
                            row={evt}
                            notes={evt.notes}
                            onClick={() => onAddNote(idx)}
                            eventNotesActive={eventNotesActive}
                            tzOffset={tzOffset}
                            ariaPrefix={`Event ${evt.event_id || ''}`}
                          />
                        </td>
                      )}
                      {isGrandstream && (
                        <td className="dp-td" style={{ textAlign: 'center' }}>
                          <CallsCell
                            calls={evt.calls}
                            tzOffset={tzOffset}
                            ariaPrefix={`Event ${evt.event_id || ''}`}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                  {eventData.length === 0 && (
                    <tr className="dp-tr">
                      <td className="dp-td" colSpan={5 + (eventNotesActive ? 1 : 0) + (isGrandstream ? 1 : 0)} style={{ textAlign: 'center' }}>
                        <span className="dp-cell-text dp-cell-text--muted">No recent events</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportingTab;
